import type { AgentRunChangedFile, AgentRunDetail, AgentRunEventDto, ArtifactDto } from '@fos/shared/contracts/agents';
import { approvalCardPath } from '@fos/shared/contracts/approvals';
import {
  AGENT_RUN_EVENT_SOURCE_LABELS,
  AGENT_RUN_EVENT_TYPE_LABELS,
  AGENT_RUN_FILE_CHANGE_LABELS,
  APPROVAL_DISPLAY_STATUS_LABELS,
  ARTIFACT_KIND_LABELS,
} from '@fos/shared/labels';
import { Download, ExternalLink, FileText, GitPullRequest, Link2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { cn } from '../../../lib/cn';
import { formatRelativePast, formatTime } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Button, Dot, ErrorState, Mono, Section, SectionHeader, Skeleton, type Tone } from '../../../ui';
import { TOUCH_BUTTON, TOUCH_TEXT } from '../../mobile';

/** Sekcje szczegółów przebiegu: zmienione pliki, artefakty (podgląd diffu i logu), akceptacje, dziennik. */

const CHANGE_TONE: Record<AgentRunChangedFile['change'], Tone> = {
  added: 'success',
  modified: 'accent',
  deleted: 'danger',
  renamed: 'neutral',
};

export function ChangedFiles({ files }: { files: AgentRunChangedFile[] }) {
  if (files.length === 0) return <p className="text-body-s text-fg-muted">Brak listy zmienionych plików.</p>;
  return (
    <ul className="border-t border-line" aria-label="Zmienione pliki">
      {files.map((f) => (
        <li key={`${f.change}:${f.path}`} className="flex min-h-10 items-center gap-3 border-b border-line py-2">
          <Dot tone={CHANGE_TONE[f.change]} />
          <Mono className="min-w-0 flex-1 truncate text-caption text-fg" title={f.path}>
            {f.previousPath ? `${f.previousPath} → ${f.path}` : f.path}
          </Mono>
          <span className="shrink-0 text-caption text-fg-muted">{AGENT_RUN_FILE_CHANGE_LABELS[f.change]}</span>
          {(f.additions != null || f.deletions != null) && (
            <Mono className="shrink-0 text-caption">
              <span className="text-success">+{f.additions ?? 0}</span>{' '}
              <span className="text-danger">−{f.deletions ?? 0}</span>
            </Mono>
          )}
        </li>
      ))}
    </ul>
  );
}

function lineClass(line: string, kind: ArtifactDto['kind']): string {
  if (kind !== 'diff') return 'text-fg-secondary';
  if (line.startsWith('+++') || line.startsWith('---')) return 'text-fg-muted';
  if (line.startsWith('+')) return 'text-success';
  if (line.startsWith('-')) return 'text-danger';
  if (line.startsWith('@@')) return 'text-accent';
  if (line.startsWith('diff ')) return 'font-semibold text-fg';
  return 'text-fg-secondary';
}

/** Podgląd tekstowy artefaktu (diff z kolorami linii, log testów, raport) — po redakcji sekretów na serwerze. */
export function ArtifactPreview({ artifact }: { artifact: ArtifactDto }) {
  const text = trpc.agents.artifactText.useQuery({ id: artifact.id });
  if (text.isLoading) return <Skeleton className="h-40 w-full" />;
  if (text.isError || !text.data) {
    return <ErrorState compact title="Nie udało się wczytać podglądu" onRetry={() => void text.refetch()} />;
  }
  const lines = text.data.text.split(/\r?\n/);
  return (
    <figure aria-label={`Podgląd: ${artifact.title}`} className="flex flex-col gap-2">
      <pre className="max-h-[480px] overflow-auto rounded-lg bg-surface p-3 font-mono text-[12px] leading-[1.55]">
        {lines.map((line, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: linie podglądu nie mają własnego id
          <div key={i} className={cn('whitespace-pre', lineClass(line, artifact.kind))}>
            {line || ' '}
          </div>
        ))}
      </pre>
      {text.data.truncated && (
        <figcaption className="text-caption text-fg-muted">
          Podgląd ucięty — pełny plik pobierzesz przyciskiem obok.
        </figcaption>
      )}
    </figure>
  );
}

const TEXT_KINDS = new Set(['diff', 'test_log', 'report']);

export function ArtifactsList({ artifacts }: { artifacts: ArtifactDto[] }) {
  const [open, setOpen] = useState<string | null>(null);
  if (artifacts.length === 0) return <p className="text-body-s text-fg-muted">Brak artefaktów.</p>;
  return (
    <ul className="border-t border-line" aria-label="Artefakty">
      {artifacts.map((a) => {
        const Icon = a.kind === 'pr' ? GitPullRequest : a.kind === 'link' ? Link2 : FileText;
        const previewable = TEXT_KINDS.has(a.kind) && a.fileId;
        const expanded = open === a.id;
        return (
          <li key={a.id} className="border-b border-line py-2.5">
            <div className="flex min-h-9 flex-wrap items-center gap-3">
              <Icon size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-body-s text-fg">{a.title}</span>
                <span className="text-caption text-fg-muted">
                  {ARTIFACT_KIND_LABELS[a.kind]} · {formatRelativePast(a.createdAt)}
                </span>
              </span>
              {a.url && (
                <Button asChild variant="ghost" size="s" iconRight={ExternalLink} className={TOUCH_BUTTON}>
                  <a href={a.url} target="_blank" rel="noopener noreferrer">
                    Otwórz
                  </a>
                </Button>
              )}
              {previewable && (
                <Button
                  variant="ghost"
                  size="s"
                  aria-expanded={expanded}
                  onClick={() => setOpen(expanded ? null : a.id)}
                  className={TOUCH_BUTTON}
                >
                  {expanded ? 'Zwiń' : 'Podgląd'}
                </Button>
              )}
              {a.downloadPath && (
                <Button asChild variant="ghost" size="s" icon={Download} className={TOUCH_BUTTON}>
                  <a href={a.downloadPath} aria-label={`Pobierz: ${a.title}`}>
                    Pobierz
                  </a>
                </Button>
              )}
            </div>
            {expanded && (
              <div className="mt-2">
                <ArtifactPreview artifact={a} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function RunApprovals({ approvals }: { approvals: AgentRunDetail['approvals'] }) {
  if (approvals.length === 0) return null;
  return (
    <Section aria-labelledby="akceptacje-runu" className="mt-4 md:mt-6">
      <SectionHeader title={<span id="akceptacje-runu">Akceptacje</span>} />
      <ul className="border-t border-line">
        {approvals.map((a) => (
          <li key={a.id} className="flex min-h-12 items-center gap-3 border-b border-line py-2">
            <span className="min-w-0 flex-1 truncate text-body-s text-fg">{a.summary}</span>
            <span className="shrink-0 text-caption text-fg-secondary">
              {APPROVAL_DISPLAY_STATUS_LABELS[a.displayStatus]}
              {a.simulated ? ' · symulacja' : ''}
            </span>
            <Link
              to={approvalCardPath(a.id)}
              className={`shrink-0 rounded-xs text-caption font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
            >
              Przejdź
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  );
}

const EVENT_TONE: Partial<Record<AgentRunEventDto['type'], Tone>> = {
  status: 'accent',
  error: 'danger',
  warning: 'warning',
  test: 'success',
  approval: 'accent',
};

function EventData({ data }: { data: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`rounded-xs text-caption text-fg-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
      >
        {open ? 'Ukryj szczegóły' : 'Szczegóły'}
      </button>
      {open && (
        <pre className="mt-1 max-h-64 overflow-auto rounded-md bg-surface p-2 font-mono text-[11px] leading-[1.5] text-fg-secondary">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

/** Dziennik przebiegu: polecenia, operacje i dowody wyniku (bez toku rozumowania, po redakcji sekretów). */
export function RunTimeline({ events, truncated }: { events: AgentRunEventDto[]; truncated: boolean }) {
  if (events.length === 0) return <p className="text-body-s text-fg-muted">Dziennik jest pusty.</p>;
  return (
    <div>
      {truncated && <p className="mb-2 text-caption text-fg-muted">Pokazujemy najnowsze zdarzenia.</p>}
      <ol className="relative flex flex-col" aria-label="Dziennik przebiegu">
        {events.map((e) => (
          <li key={e.seq} className="flex gap-3 border-b border-line py-2.5">
            <Mono className="w-11 shrink-0 pt-0.5 text-caption text-fg-muted">
              {formatTime(e.occurredAt ?? e.createdAt)}
            </Mono>
            <Dot tone={EVENT_TONE[e.type] ?? 'neutral'} className="mt-1.5" />
            <div className="min-w-0 flex-1">
              <p className="text-body-s break-words whitespace-pre-wrap text-fg">{e.message || '—'}</p>
              <p className="text-caption text-fg-muted">
                {AGENT_RUN_EVENT_TYPE_LABELS[e.type]} · {AGENT_RUN_EVENT_SOURCE_LABELS[e.source]} · #{e.seq}
              </p>
              {e.data && Object.keys(e.data).length > 0 && <EventData data={e.data} />}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
